import type { EvalVariantBriefing } from "contracts/api/evals/judge";
import { For, Show } from "solid-js";
import { PanelSection } from "../playground/PanelSection";
import { EvalVariantScoreTables } from "./EvalVariantScoreTables";

/**
 * One variant's whole analysis output, in the same order and the same table
 * idioms the playground run tables use — an analyst comparing two of these
 * side by side should not have to re-learn the layout per column.
 *
 * This is exactly what the judge was shown, minus the identity header above it,
 * which the judge is deliberately not given.
 */
export function EvalVariantOutput(props: {
  side: string;
  briefing: EvalVariantBriefing;
}) {
  return (
    <div>
      <div class="panel-head">
        <span class="panel-title">
          {props.side} — {props.briefing.label}
        </span>
        <span class="badge mono">{props.briefing.agentModel}</span>
        <span class="badge">
          {props.briefing.promptBundleLabel ?? "active prompt"}
        </span>
      </div>

      <PanelSection title="Summary">
        <Show
          when={props.briefing.output.summary}
          fallback={<span class="landing-sub">No summary.</span>}
        >
          {(summary) => <p>{summary()}</p>}
        </Show>
      </PanelSection>

      <Show when={props.briefing.output.findings.length > 0}>
        <PanelSection
          title="Findings"
          count={props.briefing.output.findings.length}
          flush
        >
          <table class="tbl">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Severity</th>
                <th>Finding</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.briefing.output.findings}>
                {(finding) => (
                  <tr>
                    <td class="mono">{finding.metricKey}</td>
                    <td>
                      <span class="badge">{finding.severity}</span>
                    </td>
                    <td>
                      <div>{finding.title}</div>
                      <div class="landing-sub">{finding.detail}</div>
                      <Show when={finding.recommendedAction}>
                        {(action) => (
                          <div class="landing-sub">Fix: {action()}</div>
                        )}
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </PanelSection>
      </Show>

      <EvalVariantScoreTables output={props.briefing.output} />
    </div>
  );
}
